import _extends from "@babel/runtime-corejs3/helpers/extends";
import _Object$values from "@babel/runtime-corejs3/core-js-stable/object/values";
import _reduceInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/reduce";
import _indexOfInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/index-of";
import _setTimeout from "@babel/runtime-corejs3/core-js-stable/set-timeout";
import _mapInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/map";

/* eslint-disable max-lines */
import React, { useRef, useMemo, useEffect, useCallback } from 'react';
import { useVirtual } from 'react-virtual';
import { emulateTab } from 'emulate-tab';
import Box from '@rexlabs/box';
import { useStyles, StyleSheet } from '@rexlabs/styling';
import { TextInput } from './text-input';
import { SelectedTags } from './selected-tags';
import { HelperText } from './helper-text';
import { FreeformFixture } from './freeform-fixture';
import { SimpleOption } from './options/simple';
import { GroupHeading } from './group-heading';
import { useSelect } from '../hooks/use-select';
import { showFreeform, isHeadingItem } from '../utils';
import { isNil } from 'lodash';
var openMenuCount = 0;
var INPUT_HEIGHT = 38;
var ITEMS_TO_SHOW = 7;
var defaultStyles = StyleSheet({
  combobox: {
    // Let flex item respect parent's max height
    minHeight: 0
  },
  wrapper: {
    minHeight: 0
  },
  container: {
    overflow: 'auto',
    zIndex: 0
  },
  menu: {
    position: 'relative',
    width: '100%'
  },
  option: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%'
  },
  hidden: {
    display: 'none'
  }
});
export function Menu(_ref) {
  var _selectedTagsRef$curr, _selectedTagsRef$curr2, _helperTextRef$curren, _helperTextRef$curren2, _freeformRef$current$, _freeformRef$current;

  var propHelperText = _ref.helperText,
      placeholder = _ref.placeholder,
      placement = _ref.placement,
      setMaxHeight = _ref.setMaxHeight,
      toggleButtonEl = _ref.toggleButtonEl,
      _ref$Option = _ref.Option,
      Option = _ref$Option === void 0 ? SimpleOption : _ref$Option,
      Prefix = _ref.Prefix;
  var s = useStyles(defaultStyles);

  var _useSelect = useSelect(),
      id = _useSelect.id,
      multi = _useSelect.multi,
      searchable = _useSelect.searchable,
      freeform = _useSelect.freeform,
      menuOpenOnFocus = _useSelect.menuOpenOnFocus,
      items = _useSelect.items,
      isOpen = _useSelect.isOpen,
      inputValue = _useSelect.inputValue,
      highlightedIndex = _useSelect.highlightedIndex,
      selectedItem = _useSelect.selectedItem,
      selectedItems = _useSelect.selectedItems,
      getItemId = _useSelect.getItemId,
      selectItem = _useSelect.selectItem,
      itemToString = _useSelect.itemToString,
      getComboboxProps = _useSelect.getComboboxProps,
      getMenuProps = _useSelect.getMenuProps,
      getInputProps = _useSelect.getInputProps,
      getItemProps = _useSelect.getItemProps,
      getHeadingProps = _useSelect.getHeadingProps,
      getFixtureProps = _useSelect.getFixtureProps,
      setScrollToIndex = _useSelect.setScrollToIndex;

  var stopFocus = useRef(false);
  var itemHeights = useRef([]);
  var inputRef = useRef(null);
  var parentRef = useRef(null);
  var selectedTagsRef = useRef(null);
  var helperTextRef = useRef(null);
  var freeformRef = useRef(null); // If these elements exist, add them to the maxHeight of the select

  var fixtureHeights = INPUT_HEIGHT + ((_selectedTagsRef$curr = (_selectedTagsRef$curr2 = selectedTagsRef.current) === null || _selectedTagsRef$curr2 === void 0 ? void 0 : _selectedTagsRef$curr2.offsetHeight) !== null && _selectedTagsRef$curr !== void 0 ? _selectedTagsRef$curr : 0) + ((_helperTextRef$curren = (_helperTextRef$curren2 = helperTextRef.current) === null || _helperTextRef$curren2 === void 0 ? void 0 : _helperTextRef$curren2.offsetHeight) !== null && _helperTextRef$curren !== void 0 ? _helperTextRef$curren : 0) + ((_freeformRef$current$ = (_freeformRef$current = freeformRef.current) === null || _freeformRef$current === void 0 ? void 0 : _freeformRef$current.offsetHeight) !== null && _freeformRef$current$ !== void 0 ? _freeformRef$current$ : 0);
  var helperText = useMemo(function () {
    if (!isNil(propHelperText)) {
      return propHelperText;
    }

    if (items.length <= 0) {
      return 'No results found';
    } // It doesn't make too much sense to show the default
    // idle text when the select isn't searchable, unless
    // we haven't defined any items at all, as this
    // should usually be used as a straight dropdown


    if (!searchable) {
      return null;
    } // Only show the select results text on multi select when
    // there are no currently selected items
    // This essentially will allow the components to hot swap
    // with one another and should be less jarring when
    // selecting the first item


    return multi ? selectedItems.length <= 0 ? 'Select results' : null : 'Select result';
  }, [propHelperText, multi, searchable, items, selectedItems]);
  var showSelectedTags = multi && selectedItems.length > 0;
  var showFixture = useMemo(function () {
    return showFreeform({
      freeform: freeform,
      inputValue: inputValue,
      items: items,
      selectedItems: selectedItems,
      selectedItem: selectedItem,
      itemToString: itemToString
    });
  }, [freeform, inputValue, items, selectedItem, selectedItems, itemToString]);

  var _useVirtual = useVirtual({
    parentRef: parentRef,
    overscan: 2,
    size: items.length,
    // @ts-ignore
    measureSize: useCallback(function (el) {
      var id = el.id;
      var height = el.offsetHeight;
      itemHeights.current[id] = height;

      var itemHeightsArray = _Object$values(itemHeights.current); // This probably runs on render so make sure we have
      // a specific condition so it can only be run once


      // This probably runs on render so make sure we have
      // a specific condition so it can only be run once
      if (itemHeightsArray.length === ITEMS_TO_SHOW) {
        // This is probably not the indended use of this function
        // and I doubt it's meant to have side effects but
        // it works
        setMaxHeight === null || setMaxHeight === void 0 ? void 0 : setMaxHeight(_reduceInstanceProperty(itemHeightsArray).call(itemHeightsArray, function (acc, height, index) {
          if (index === itemHeightsArray.length - 1) {
            acc += height / 2;
          } else {
            acc += height;
          }

          return acc;
        }, 0) + fixtureHeights);
      }

      return height;
    }, [fixtureHeights, setMaxHeight])
  }),
      virtualItems = _useVirtual.virtualItems,
      totalSize = _useVirtual.totalSize,
      scrollToIndex = _useVirtual.scrollToIndex;

  var comboboxProps = getComboboxProps();
  var menuProps = getMenuProps();
  var inputProps = getInputProps({
    placeholder: placeholder,
    ref: inputRef,
    onKeyDown: function onKeyDown(e) {
      if (e.key === 'Tab') {
        if (toggleButtonEl) {
          // Stop the tab key from moving focus to the next element
          // (in this case would be to move out of the browser because
          // the input we're tabbing from is portalled)
          e.preventDefault();

          if (highlightedIndex > -1) {
            selectItem(items[highlightedIndex]);
          } // Emulate a tab key from the toggle button and move to
          // the correct element


          var emulation = emulateTab.from(toggleButtonEl);

          if (!e.shiftKey) {
            emulation.toNextElement();
          } else {
            // Re-introduce this once the bug in emulate-tab is fixed
            // emulation.toPreviousElement();
            var tabbableElements = emulateTab.findSelectableElements();

            var currentIndex = _indexOfInstanceProperty(tabbableElements).call(tabbableElements, toggleButtonEl); // If we are on the first element


            if (currentIndex === 0) {
              // Wrap around to the last element
              emulation.to(tabbableElements[tabbableElements.length - 1]);
            } else {
              // Tab to the element before the toggle button
              emulation.to(tabbableElements[currentIndex - 1]);
            }
          } // We can't focus on the toggle button after tabbing
          // otherwise as the menu closes instead of focusing
          // the next element we just focus the toggle button


          stopFocus.current = true;
        }
      }
    }
  }); // Scroll to the highlighted index when the menu mounts

  useEffect(function () {
    if (highlightedIndex >= 0) {
      scrollToIndex(highlightedIndex);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []);
  useEffect(function () {
    openMenuCount++; // Without the timeout sometimes the input will get focus
    // however the cursor doesn't appear within the text input

    _setTimeout(function () {
      if (inputRef.current && isOpen) {
        inputRef.current.focus({
          preventScroll: true
        });
        inputRef.current.select();
      }
    });

    return function () {
      if (isOpen && searchable && !stopFocus.current && // We want to focus the toggle button when the menu
      // closes but only if we're not tabbing between selects
      openMenuCount === 1) {
        menuOpenOnFocus.current = false;
        toggleButtonEl === null || toggleButtonEl === void 0 ? void 0 : toggleButtonEl.focus();
      }

      openMenuCount--;
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  useEffect(function () {
    // Send the scroll to index function up so that we can
    // deal with the scrolling logic within our state change
    // function in downshift to avoid scrolling when
    // hovering over items
    setScrollToIndex(function () {
      return scrollToIndex;
    });
  }, [scrollToIndex, setScrollToIndex]);
  return /*#__PURE__*/React.createElement(Box, _extends({}, s('combobox'), comboboxProps, {
    flex: "1",
    flexDirection: placement === 'top' ? 'column-reverse' : 'column'
  }), /*#__PURE__*/React.createElement(TextInput, _extends({}, inputProps, {
    "data-testid": "select.text-input.".concat(id),
    Prefix: Prefix
  })), /*#__PURE__*/React.createElement(Box, _extends({}, s('wrapper'), {
    flexDirection: "column"
  }), showSelectedTags && /*#__PURE__*/React.createElement(SelectedTags, {
    ref: selectedTagsRef
  }), helperText && /*#__PURE__*/React.createElement(HelperText, {
    ref: helperTextRef
  }, helperText), items.length > 0 && /*#__PURE__*/React.createElement(Box, _extends({}, s('container'), {
    ref: parentRef
  }), /*#__PURE__*/React.createElement(Box, _extends({}, menuProps, s.with('menu')({
    height: "".concat(totalSize, "px")
  }), {
    flexDirection: "column"
  }), _mapInstanceProperty(virtualItems).call(virtualItems, function (_ref2) {
    var _getItemId;

    var index = _ref2.index,
        start = _ref2.start,
        measureRef = _ref2.measureRef;
    var item = items[index];

    if (isHeadingItem(item)) {
      return /*#__PURE__*/React.createElement(GroupHeading, _extends({}, s.with('option')({
        transform: "translateY(".concat(start, "px)")
      }), getHeadingProps({
        item: item,
        index: index,
        ref: measureRef
      }), {
        key: item.type
      }), item.heading);
    }

    return /*#__PURE__*/React.createElement(Option, _extends({}, getItemProps({
      item: item,
      index: index,
      ref: measureRef
    }), s.with('option')({
      transform: "translateY(".concat(start, "px)")
    }), {
      key: (_getItemId = getItemId(item)) !== null && _getItemId !== void 0 ? _getItemId : index,
      index: index,
      item: item
    }));
  }))), /*#__PURE__*/React.createElement(FreeformFixture, _extends({}, getFixtureProps({
    ref: freeformRef,
    index: items.length,
    // If we aren't showing the fixture then disable the interaction
    disabled: !showFixture
  }), s({
    hidden: !showFixture
  }), {
    index: items.length,
    "data-testid": "select.freeform-fixture.".concat(id)
  }))));
}